import {lazy, Suspense, useEffect} from 'react';
import {Route, Routes, useLocation, useNavigate, Navigate} from 'react-router';
import {ThemeProvider} from './context/ThemeContext';
import {useTeamsContext} from './context/TeamsContext';
import {app} from '@microsoft/teams-js';
import Layout from './components/Layout';
import {ToastContainer} from 'react-toastify';
import {useAuthContext} from './context/AuthContext';
import {AuthenticatedTemplate} from './components/AuthenticatedTemplate';
import {UnauthenticatedTemplate} from './components/UnAuthenticatedTemplate';
import {FeatureFlagsProvider} from './context/FeatureFlagsContext';
import {useFeatureFlags} from './context/FeatureFlagsContext';
import pages, {Page} from './pages';
import {NavigationRoutes} from './NavigationRoutes';
import {Spinner} from 'reactstrap';
import {
    captureShellFromUrl,
    getShellName,
    hasShellRedirectFired,
    markShellRedirectFired,
    readLastAssistant
} from './lib/useShellSession';
import PersistentVoiceAssistantControl from './voice/PersistentVoiceAssistantControl';
import VoiceBrowserActions from './voice/VoiceBrowserActions';
import VoiceThemeActions from './voice/VoiceThemeActions';
import {isEmbeddedInExtension} from './lib/extensionEmbed';
import useVoiceAssistantAvailability from './voice/useVoiceAssistantAvailability';
import useClientConfig from './lib/useClientConfig';
import {consumeLoginRedirect} from './lib/loginRedirect';

captureShellFromUrl();

const LoginPage = lazy(() => import('./components/LoginPage'));
const AccessDeniedPage = lazy(() => import('./components/AccessDeniedPage'));
const HomePage = lazy(() => import('./components/HomePage'));
const VerifyEmailLinkPage = lazy(
    () => import('./components/VerifyEmailLinkPage')
);
const ErrorBoundaryTestPage = lazy(
    () => import('./components/ErrorBoundaryTestPage')
);

const Fallback = () => (
    <div
        className="d-flex flex-fill align-items-center justify-content-center"
        style={{minHeight: '100vh'}}
    >
        <Spinner color="primary" />
    </div>
);

const PageRouteElement: React.FC<{page: Page}> = ({page}) => {
    const {isAuthorizedForResource, isAuthenticated, userProfile} =
        useAuthContext();
    const {isEnabled, isLoading} = useFeatureFlags();
    const {multiProviderEnabled, isLoading: isClientConfigLoading} =
        useClientConfig();
    const PageComponent = page.component;

    const isRoleGated = page.authorize && (page.roles?.length ?? 0) > 0;

    // Roles come from the user profile. Until it has loaded there is nothing to
    // check against, so wait rather than judging an empty role list.
    if (isRoleGated && isAuthenticated && !userProfile) {
        return <Fallback />;
    }

    if (!isAuthorizedForResource(page)) {
        if (isRoleGated && isAuthenticated) {
            return <AccessDeniedPage />;
        }
        return <Navigate to={NavigationRoutes.home()} replace />;
    }

    if (page.featureFlag && isLoading) {
        return <Fallback />;
    }

    if (page.featureFlag && !isEnabled(page.featureFlag)) {
        return <Navigate to={NavigationRoutes.home()} replace />;
    }

    if (page.requiresMultiProvider && isClientConfigLoading) {
        return <Fallback />;
    }

    if (page.requiresMultiProvider && !multiProviderEnabled) {
        return <Navigate to={NavigationRoutes.home()} replace />;
    }

    return <PageComponent />;
};

const App: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {isAuthenticated, currentUserEmail} = useAuthContext();
    const {isRunningInTeams} = useTeamsContext();
    const {voiceAssistantEnabled} = useVoiceAssistantAvailability();

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        // A deep link remembered by the login page. Consumed on any
        // authenticated route so a stale entry never redirects a later visit;
        // only honoured when the user actually landed on the home page.
        const redirectPath = consumeLoginRedirect();

        if (redirectPath && location.pathname === '/') {
            navigate(redirectPath, {replace: true});
            return;
        }

        const shell = getShellName();
        if (
            shell &&
            currentUserEmail &&
            !hasShellRedirectFired() &&
            location.pathname === '/'
        ) {
            const lastAssistantId = readLastAssistant(currentUserEmail, shell);
            if (lastAssistantId) {
                markShellRedirectFired();
                navigate(NavigationRoutes.assistants.detail(lastAssistantId), {
                    replace: true
                });
            }
        }
    }, [isAuthenticated, location.pathname, navigate, currentUserEmail]);

    // Handle OAuth redirect return - restore original page after OAuth flow
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const oauthSuccess = urlParams.get('oauth_success');

        // Only handle on root path to avoid interference with other routes
        if (oauthSuccess === 'true' && location.pathname === '/') {
            const returnUrl = localStorage.getItem('oauth_return_url');

            if (returnUrl && returnUrl !== '/') {
                // Clear the stored URL
                localStorage.removeItem('oauth_return_url');

                // Redirect to the original page with oauth_success parameter
                const separator = returnUrl.includes('?') ? '&' : '?';
                navigate(`${returnUrl}${separator}oauth_success=true`, {
                    replace: true
                });
            }
            // If no return URL or it's root, stay on root with oauth_success param
            // (MessageBoxToolButton will handle refresh)
        }
    }, [location.search, location.pathname, navigate]);

    useEffect(() => {
        if (isRunningInTeams) {
            app.lifecycle.registerBeforeSuspendOrTerminateHandler(async () => {
                sessionStorage.setItem(
                    'lastVisitedPath',
                    window.location.pathname
                );
            });

            app.lifecycle.registerOnResumeHandler(async () => {
                const lastPath = sessionStorage.getItem('lastVisitedPath');
                if (lastPath) {
                    navigate(lastPath);
                }
                app.notifySuccess();
            });
        }
    }, [isRunningInTeams, navigate]);

    const renderRoute = (page: Page, parentPath?: string) => {
        const fullPath = parentPath
            ? page.path.replace(parentPath, '').replace(/^\//, '')
            : page.path;

        const element = <PageRouteElement page={page} />;

        if (page.index) {
            return <Route key={`${page.path}-index`} index element={element} />;
        }

        return (
            <Route key={page.path} path={fullPath} element={element}>
                {page.children?.map(childPage =>
                    renderRoute(childPage, page.path)
                )}
            </Route>
        );
    };

    // Separate pages that need Layout from those that don't
    const pagesWithLayout = pages.filter(p => !p.noLayout);
    const pagesWithoutLayout = pages.filter(p => p.noLayout);

    return (
        <FeatureFlagsProvider>
            <ThemeProvider>
                <VoiceThemeActions />
                {/* Embedded mode is a module constant, so this never flips
                    between renders and the hook order stays stable. */}
                {isEmbeddedInExtension() && <VoiceBrowserActions />}
                <ToastContainer pauseOnFocusLoss={false} />
                <AuthenticatedTemplate>
                    {voiceAssistantEnabled && (
                        <PersistentVoiceAssistantControl />
                    )}
                    <Suspense fallback={<Fallback />}>
                        <Routes>
                            <Route element={<Layout />}>
                                {pagesWithLayout.map(page => renderRoute(page))}
                                <Route path="*" element={<HomePage />} />
                            </Route>
                            {pagesWithoutLayout.map(page => renderRoute(page))}
                        </Routes>
                    </Suspense>
                </AuthenticatedTemplate>

                <UnauthenticatedTemplate>
                    <Suspense fallback={<Fallback />}>
                        <Routes>
                            {/* Magic link verification — must be accessible without auth */}
                            <Route
                                path="/auth/verify-link"
                                element={<VerifyEmailLinkPage />}
                            />
                            {/* Error-boundary test — reachable without auth so the
                                recovery screen can be reviewed in any auth state */}
                            <Route
                                path="/error-test"
                                element={<ErrorBoundaryTestPage />}
                            />
                            <Route element={<Layout />}>
                                <Route path="*" element={<LoginPage />} />
                            </Route>
                        </Routes>
                    </Suspense>
                </UnauthenticatedTemplate>
            </ThemeProvider>
        </FeatureFlagsProvider>
    );
};

export default App;
